import {
  useEffect,
  useCallback,
  ReactNode,
  useRef,
  useState,
  useMemo,
  MouseEvent,
  forwardRef,
  CSSProperties,
} from "react";
import {
  useClassNames,
  useClickOutside,
  useForwardedRef,
} from "../../shared/hooks";
import style from "./index.module.scss";

export enum MenuPosition {
  LEFT = "left",
  RIGHT = "right",
}

type MenuProps = {
  sx?: CSSProperties;
  maxWidth?: number;
  children: ReactNode;
  activator: ReactNode;
  show?: boolean;
  className?: string;
  isLocallyControlled?: boolean;
  onChange?: (show: boolean) => void;
};
let windowResizeTimeout: ReturnType<typeof setTimeout> | null = null;
let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
let resizeHandler: () => void;
let scrollHandler: () => void;

const Menu = forwardRef<HTMLDivElement, MenuProps>(
  (
    {
      sx,
      maxWidth = 800,
      children,
      activator,
      show: propShow,
      isLocallyControlled,
      className,
      onChange,
    },
    ref
  ) => {
    const [localShow, setLocalShow] = useState<boolean>(false);

    const classNames = useClassNames([style.Menu, className ? className : ""]);
    const menuClassNames = useClassNames([style["Menu-display"]]);

    const parentRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const triangleRef = useRef<HTMLDivElement>(null);
    const forwardedRef = useForwardedRef(ref);

    const localRef = useMemo(() => {
      return isLocallyControlled ? parentRef : forwardedRef;
    }, [parentRef, forwardedRef, isLocallyControlled]);

    const handleMenuDisplay = useCallback(() => {
      const menu = menuRef.current;
      const parent = localRef.current;
      const triangle = triangleRef.current;

      if (menu && parent && triangle) {
        const windowWidth = window.innerWidth;
        const parentRect = parent.getBoundingClientRect();
        triangle.style.top = `${parentRect?.bottom || 0}px`;
        triangle.style.left = `${parentRect?.left + 12 || 0}px`;
        menu.style.top = `${parentRect?.bottom + 10 || 0}px`;
        menu.style.maxWidth = `${maxWidth}px`;
        if (parentRect.left + maxWidth + 20 > windowWidth) {
          menu.style.right = "20px";
          if (windowWidth < maxWidth + 20) {
            menu.style.left = "0px";
          } else {
            menu.style.left = "auto";
          }
        } else {
          menu.style.right = "auto";
        }
      }
    }, [maxWidth, localRef]);

    const show = useMemo(() => {
      return propShow === undefined || isLocallyControlled
        ? localShow
        : propShow;
    }, [isLocallyControlled, localShow, propShow]);

    const handleShow = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        if (!show) {
          if (isLocallyControlled || propShow === undefined) {
            setLocalShow(true);
          } else {
            if (onChange) onChange(true);
          }
        } else {
          if (isLocallyControlled || propShow === undefined) {
            setLocalShow(false);
          } else {
            if (onChange) onChange(false);
          }
        }
      },
      [show, isLocallyControlled, onChange, propShow]
    );

    const clickHandler = useCallback(() => {
      if (isLocallyControlled || propShow === undefined) {
        setLocalShow(false);
      } else {
        if (onChange) onChange(false);
      }
    }, [isLocallyControlled, propShow, onChange]);

    useEffect(() => {
      resizeHandler = () => {
        if (windowResizeTimeout) clearTimeout(windowResizeTimeout);
        windowResizeTimeout = setTimeout(() => {
          handleMenuDisplay();
        }, 300);
      };
      scrollHandler = () => {
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          handleMenuDisplay();
        }, 50);
      };
      if (show) {
        window.addEventListener("resize", resizeHandler);
        window.addEventListener("scroll", scrollHandler);
        handleMenuDisplay();
        localRef.current?.classList.add(style.show);
      } else {
        localRef.current?.classList.remove(style.show);
      }
      return () => {
        window.removeEventListener("resize", resizeHandler);
        window.removeEventListener("scroll", scrollHandler);
      };
    }, [show, handleMenuDisplay, localRef]);

    useClickOutside(localRef, clickHandler);

    return (
      <div ref={localRef} className={classNames}>
        <div ref={triangleRef} className={style["Menu-triangle"]} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={handleShow}
        >
          {activator}
        </div>
        <div ref={menuRef} className={menuClassNames} style={sx}>
          {children}
        </div>
      </div>
    );
  }
);

export default Menu;
